import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout';

const guides = [
  {
    title: 'البدء السريع',
    description:
      'إنشاء الحساب، اختيار الدور المناسب، ضبط الملف الشخصي، والوصول إلى لوحة التحكم.',
    items: [
      'إنشاء حساب من /register أو تسجيل الدخول من /login.',
      'اختيار دور المستخدم: معلم، طالب، ولي أمر، موظف، أو مدير مصرح.',
      'إكمال بيانات الحساب الأساسية من صفحة الإعدادات بعد تسجيل الدخول.',
    ],
  },
  {
    title: 'إدارة العملية التعليمية',
    description:
      'الأدوات الأساسية للمعلمين والطلاب وأولياء الأمور داخل Class-Tech.',
    items: [
      'إدارة الطلاب والفصول والجداول من لوحة تحكم المعلم.',
      'متابعة الحضور، الواجبات، الدرجات، والتقارير من المساحات المخصصة.',
      'ربط الطلاب بأولياء الأمور عبر أكواد ربط مخصصة وآمنة.',
    ],
  },
  {
    title: 'الدفع والاشتراكات',
    description:
      'معلومات تشغيلية حول الخطط، المدفوعات، الفواتير، وإدارة الاشتراك.',
    items: [
      'عرض الخطط العامة من صفحة الأسعار.',
      'إدارة الاشتراك والمدفوعات من داخل الحساب المصرح له.',
      'استخدام مزودي دفع خارجيين لمعالجة المعاملات دون تخزين بيانات البطاقة في الواجهة.',
    ],
  },
  {
    title: 'الدعم والأمان',
    description:
      'قنوات الدعم، الخصوصية، وحماية الحسابات والبيانات التعليمية.',
    items: [
      'إرسال طلب دعم من صفحة التواصل أو مركز المساعدة.',
      'مراجعة سياسة الخصوصية وشروط الاستخدام من الروابط العامة.',
      'استخدام إعدادات الحساب لإدارة كلمة المرور، الخصوصية، والإشعارات.',
    ],
  },
];

const reviewLinks = [
  { label: 'توثيق تكامل Zoom', to: '/zoom-documentation' },
  { label: 'سياسة الخصوصية', to: '/privacy' },
  { label: 'مركز الدعم', to: '/support' },
  { label: 'شروط الاستخدام', to: '/terms' },
  { label: 'التواصل مع الدعم', to: '/contact#contact-form' },
];

const Documentation: React.FC = () => {
  return (
    <PublicLayout>
      <div className='bg-secondary-50 text-secondary-900 dark:bg-secondary-950 dark:text-white'>
        <section className='bg-white py-16 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='max-w-3xl'>
              <h1 className='text-3xl font-bold text-secondary-950 dark:text-white md:text-5xl'>
                توثيق Class-Tech
              </h1>
              <p className='mt-5 text-lg leading-8 text-secondary-700 dark:text-secondary-200'>
                هذه الصفحة تقدم مرجعًا عامًا للمراجعين والمستخدمين حول تشغيل
                منصة Class-Tech، أهم المسارات العامة، وقنوات الدعم المطلوبة
                لاستخدام الخدمة بأمان ووضوح.
              </p>
              <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                <Link
                  to='/support'
                  className='inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700'
                >
                  فتح مركز الدعم
                </Link>
                <Link
                  to='/contact#contact-form'
                  className='inline-flex min-h-11 items-center justify-center rounded-lg border border-secondary-300 px-5 text-sm font-semibold text-secondary-800 hover:bg-secondary-100 dark:border-secondary-700 dark:text-white dark:hover:bg-secondary-800'
                >
                  إرسال طلب مساعدة
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className='py-12'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
              {guides.map(guide => (
                <article
                  key={guide.title}
                  className='rounded-lg border border-secondary-200 bg-white p-6 shadow-sm dark:border-secondary-800 dark:bg-secondary-900'
                >
                  <h2 className='text-xl font-bold text-secondary-950 dark:text-white'>
                    {guide.title}
                  </h2>
                  <p className='mt-3 leading-7 text-secondary-600 dark:text-secondary-300'>
                    {guide.description}
                  </p>
                  <ul className='mt-5 list-disc space-y-2 pr-5 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
                    {guide.items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className='bg-white py-12 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
              <div className='lg:col-span-2'>
                <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                  معلومات مهمة للمراجعة
                </h2>
                <p className='mt-4 leading-8 text-secondary-700 dark:text-secondary-200'>
                  صفحات الخصوصية، الدعم، التوثيق، والشروط متاحة دون تسجيل
                  دخول ولا تمر عبر المسارات المحمية. تستخدم Class-Tech خدمات
                  Firebase للمصادقة والتخزين وقواعد البيانات، وتستخدم مزودي
                  تكامل خارجيين فقط لتقديم وظائف محددة مثل الاجتماعات أو الدفع
                  أو الإشعارات.
                </p>
                <p className='mt-4 leading-8 text-secondary-700 dark:text-secondary-200'>
                  عند الحاجة إلى مساعدة أو توضيح أثناء المراجعة، يمكن استخدام
                  نموذج التواصل العام أو البريد المخصص للدعم، وسيتعامل الفريق
                  مع الطلب حسب الأولوية ومعلومات الاتصال المقدمة.
                </p>
              </div>
              <div className='rounded-lg bg-secondary-50 p-6 dark:bg-secondary-800'>
                <h3 className='font-bold text-secondary-950 dark:text-white'>
                  روابط الاعتماد
                </h3>
                <div className='mt-4 space-y-3'>
                  {reviewLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className='block rounded-lg bg-white px-4 py-3 text-sm font-semibold text-primary-700 hover:text-primary-900 dark:bg-secondary-900 dark:text-primary-300'
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default Documentation;
